// contexts/SystemAdminProvider.tsx
import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
    SystemAdminContext,
    type SystemAdminData,
    type SystemAdminEntityType,
    type SystemDeletableEntityType,
    type SystemEntityData,
    type SystemSaveableEntityType,
} from './SystemAdminContext';
import {
    createAdtech,
    createBroadcaster,
    createChannel,
    createDirectUser,
    createEntityAssociation,
    deleteAgency,
    deleteCustomer,
    deleteUser,
    getAdtechs,
    getAssociatedAgencies,
    getAssociatedCustomers,
    getBroadcasters,
    getChannels,
    getCountries,
    getTaxonomies,
    getUnassociatedAgencies,
    getUnassociatedCustomers,
    getUsers,
    reassignUser,
    updateAdtech,
    updateBroadcaster,
    updateChannel,
} from '@/api';
import { notifyError } from '@/utils';
import axios from 'axios';
import { wasNotified } from '@/api/core/requestUtils';

const initialSystemAdminData: SystemAdminData = {
    broadcasters: [],
    channels: [],
    countries: [],
    agencies: [],
    clients: [],
    unassociatedAgencies: [],
    unassociatedClients: [],
    users: [],
    taxonomies: [],
    adtechs: [],
    adtechgroups: [],
};

export const SystemAdminProvider = ({ children }: { children: ReactNode }) => {
    const [systemAdminData, setSystemAdminData] = useState<SystemAdminData>(initialSystemAdminData);
    // Reference-counted pending fetches — see AdminProvider for rationale.
    const [pendingFetches, setPendingFetches] = useState(0);
    const loading = pendingFetches > 0;
    const incPending = useCallback(() => setPendingFetches((c) => c + 1), []);
    const decPending = useCallback(() => setPendingFetches((c) => Math.max(0, c - 1)), []);
    // One abort controller per entity type — see AdminProvider for rationale.
    const abortControllersRef = useRef<Map<SystemAdminEntityType, AbortController>>(new Map());

    const fetchData = useCallback(
        async (entityType: SystemAdminEntityType) => {
            abortControllersRef.current.get(entityType)?.abort();
            const controller = new AbortController();
            abortControllersRef.current.set(entityType, controller);
            const signal = controller.signal;

            incPending();
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let data: any;
                switch (entityType) {
                    case 'broadcasters':
                        data = await getBroadcasters({ signal });
                        setSystemAdminData((prev) => ({ ...prev, broadcasters: data }));
                        break;
                    case 'adtechs':
                        data = await getAdtechs({ signal });
                        setSystemAdminData((prev) => ({ ...prev, adtechs: data }));
                        break;
                    case 'channels':
                        data = await getChannels({ signal });
                        setSystemAdminData((prev) => ({ ...prev, channels: data }));
                        break;
                    case 'countries':
                        data = await getCountries({ signal });
                        setSystemAdminData((prev) => ({ ...prev, countries: data }));
                        break;
                    case 'users':
                        data = await getUsers({ signal });
                        setSystemAdminData((prev) => ({ ...prev, users: data }));
                        break;
                    case 'agencies':
                        data = await getAssociatedAgencies({ signal });
                        setSystemAdminData((prev) => ({ ...prev, agencies: data }));
                        break;
                    case 'clients':
                        data = await getAssociatedCustomers({ signal });
                        setSystemAdminData((prev) => ({ ...prev, clients: data }));
                        break;
                    case 'unassociatedAgencies':
                        data = await getUnassociatedAgencies({ signal });
                        setSystemAdminData((prev) => ({ ...prev, unassociatedAgencies: data }));
                        break;
                    case 'unassociatedClients':
                        data = await getUnassociatedCustomers({ signal });
                        setSystemAdminData((prev) => ({ ...prev, unassociatedClients: data }));
                        break;
                    case 'taxonomies':
                        data = await getTaxonomies({ signal });
                        setSystemAdminData((prev) => ({ ...prev, taxonomies: data }));
                        break;
                    default:
                        console.warn(`Unknown entity type: ${entityType}`);
                }

                if (
                    Array.isArray(data) &&
                    data.length === 0 &&
                    ['broadcasters', 'adtechs', 'channels'].includes(entityType)
                ) {
                    console.warn(
                        `SystemAdminProvider.fetchData -> fetched ${entityType} is EMPTY. Full payload:`,
                        data
                    );
                }

                return data;
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error(`Error fetching ${entityType}:`, error);
                    notifyError(`Unable to retrieve ${entityType}. Please try again.`);
                }
            } finally {
                if (abortControllersRef.current.get(entityType) === controller) {
                    abortControllersRef.current.delete(entityType);
                }
                decPending();
            }
        },
        [incPending, decPending]
    );

    useEffect(() => {
        (async () => {
            try {
                await fetchData('countries');
                await fetchData('broadcasters');
                await fetchData('adtechs');
            } catch (e) {
                console.warn('SystemAdminProvider eager load failed:', e);
            }
        })();
    }, [fetchData]);

    useEffect(() => {
        const controllers = abortControllersRef.current;
        return () => {
            controllers.forEach((controller) => controller.abort());
            controllers.clear();
        };
    }, []);

    const handleSaveDirectUser = useCallback(
        async (userData: Record<string, unknown>, onSuccess?: () => void) => {
            incPending();
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await createDirectUser(userData as any);
                await fetchData('users');
                onSuccess?.();
            } catch (error) {
                const err = error as Error;
                console.error('Error creating user directly:', err);
                if (!wasNotified(err)) notifyError(`Failed to create user. ${err.message || 'Please try again.'}`);
            } finally {
                decPending();
            }
        },
        [fetchData, incPending, decPending]
    );

    const handleReassignUser = useCallback(
        async (userId: number, entityTypeId: number, entityId: number, onSuccess?: () => void) => {
            incPending();
            try {
                await reassignUser(userId, { entity_type: entityTypeId, entity_id: entityId });
                await fetchData('users');
                onSuccess?.();
            } catch (error) {
                const err = error as Error;
                console.error('Error reassigning user:', err);
                if (!wasNotified(err)) notifyError(`Failed to reassign user. ${err.message || 'Please try again.'}`);
            } finally {
                decPending();
            }
        },
        [fetchData, incPending, decPending]
    );

    const handleSaveRelationship = useCallback(
        async (relationshipData: Record<string, unknown>, onSuccess?: () => void) => {
            incPending();
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await createEntityAssociation(relationshipData as any);
                if (relationshipData.customer_id) await fetchData('clients');
                if (relationshipData.broadcaster_id) await fetchData('broadcasters');
                if (relationshipData.agency_id) await fetchData('agencies');
                onSuccess?.();
            } catch (error) {
                const err = error as Error;
                console.error('Error creating relationship:', err);
                if (!wasNotified(err))
                    notifyError(`Failed to create relationship. ${err.message || 'Please try again.'}`);
            } finally {
                decPending();
            }
        },
        [fetchData, incPending, decPending]
    );

    const handleSaveEntity = useCallback(
        async (entityData: SystemEntityData, entityType: SystemSaveableEntityType, onSuccess?: () => void) => {
            incPending();
            try {
                const isUpdate = Boolean(entityData.id);
                switch (entityType) {
                    case 'broadcaster':
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        isUpdate
                            ? await updateBroadcaster(entityData as any)
                            : await createBroadcaster(entityData as any);
                        await fetchData('broadcasters');
                        break;
                    case 'adtech':
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        isUpdate ? await updateAdtech(entityData as any) : await createAdtech(entityData as any);
                        await fetchData('adtechs');
                        break;
                    case 'channel':
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        isUpdate ? await updateChannel(entityData as any) : await createChannel(entityData as any);
                        await fetchData('channels');
                        break;
                    case 'adtechgroup':
                        break;
                    default:
                        console.error(`Unknown entity type: ${entityType}`);
                        notifyError(`Unable to save ${entityType}. Unknown entity type.`);
                        return;
                }
                onSuccess?.();
            } catch (error) {
                const err = error as Error;
                console.error(`Error saving ${entityType}:`, err);
                if (!wasNotified(err))
                    notifyError(`Failed to save ${entityType}. ${err.message || 'Please try again.'}`);
            } finally {
                decPending();
            }
        },
        [fetchData, incPending, decPending]
    );

    const handleDeleteEntity = useCallback(
        async (entityId: number, entityType: SystemDeletableEntityType) => {
            incPending();
            try {
                switch (entityType) {
                    case 'user':
                        await deleteUser(entityId);
                        await fetchData('users');
                        break;
                    case 'agency':
                        await deleteAgency(entityId);
                        await fetchData('agencies');
                        break;
                    case 'client':
                        await deleteCustomer(entityId);
                        await fetchData('clients');
                        break;
                    default:
                        console.error(`Unknown entity type for deletion: ${entityType}`);
                        notifyError(`Unable to delete ${entityType}. Unknown entity type.`);
                        return;
                }
            } catch (error) {
                const err = error as Error;
                console.error(`Error deleting ${entityType}:`, err);
                if (!wasNotified(err))
                    notifyError(`Failed to delete ${entityType}. ${err.message || 'Please try again.'}`);
            } finally {
                decPending();
            }
        },
        [fetchData, incPending, decPending]
    );

    const handleSaveBroadcaster = useCallback(
        (broadcasterData: SystemEntityData, onSuccess?: () => void) =>
            handleSaveEntity(broadcasterData, 'broadcaster', onSuccess),
        [handleSaveEntity]
    );

    const handleSaveAdTech = useCallback(
        (adtechData: SystemEntityData, onSuccess?: () => void) => handleSaveEntity(adtechData, 'adtech', onSuccess),
        [handleSaveEntity]
    );

    const handleSaveChannel = useCallback(
        (channelData: SystemEntityData, onSuccess?: () => void) => handleSaveEntity(channelData, 'channel', onSuccess),
        [handleSaveEntity]
    );

    return (
        <SystemAdminContext.Provider
            value={{
                systemAdminData,
                loading,
                fetchData,
                handleSaveEntity,
                handleDeleteEntity,
                handleSaveDirectUser,
                handleReassignUser,
                handleSaveRelationship,
                handleSaveBroadcaster,
                handleSaveAdTech,
                handleSaveChannel,
            }}
        >
            {children}
        </SystemAdminContext.Provider>
    );
};
